// Design System Colors

export const colors = {
  // Neutral/Gray Scale (Light Mode)
  // Warm stone neutrals (shadcn "stone"), not the cool blue-greys they replace.
  // The whole surface warms up from this one ramp.
  gray: {
    50: '#FAFAF9',
    100: '#F5F5F4',
    200: '#E7E5E4',
    300: '#D6D3D1',
    400: '#A8A29E',
    450: '#9C9891', // Used in breadcrumbs
    500: '#6B6B6B',
    550: '#57534E', // Used in icons
    600: '#3F3F3B',
    650: '#2A2A27', // Used in breadcrumbs active state
    700: '#1A1A1A',
  },

  // Dark mode grayscale
  darkGray: {
    50: '#1A1A19',      // Very dark gray for secondary backgrounds
    100: '#222221',     // Dark gray for cards/containers
    200: '#2A2A28',     // Border color
    300: '#3A3A37',     // Secondary border
    400: '#888888',     // Tertiary text
    450: '#999999',     // Used in breadcrumbs
    500: '#aaaaaa',     // Secondary text
    550: '#bbbbbb',     // Used in icons
    600: '#cccccc',     // Light text
    650: '#dddddd',     // Used in breadcrumbs active state
    700: '#ffffff',     // Primary text (white)
  },

  // Background
  background: {
    // Flat white, like the reference: cards are told apart by hairlines and
    // tone, not by a grey page behind them.
    main: '#FFFFFF',
    secondary: '#F5F5F4',
    white: '#ffffff',
    sidebar: '#FFFFFF',
    grid: 'none',
    gridSize: 'auto',
  },

  // Dark Background
  darkBackground: {
    main: '#0F0F0E',       // Warm near-black main background
    secondary: '#151514',  // Slightly lighter
    sidebar: '#0F0F0E',    // Sidebar background
    grid: 'none',
    gridSize: 'auto',
  },

  // Primary/Blue
  primary: {
    main: '#3B82F6',
    light: '#60A5FA',
  },

  // Selection/Highlight colors (neutral, not blue)
  selection: {
    light: '#F5F5F4',  // gray.100 - slightly darker than bgHover so selected stands out
    dark: '#262624',   // neutral dark gray - slightly darker than bgHover
  },

  // Ink: the one "accent" the chrome allows itself. A selected card, an
  // active tab, a pressed control is outlined in ink — never in blue.
  ink: {
    main: '#1A1A1A',
    outline: '1.5px solid #1A1A1A',
    darkMain: '#ECEAE3',
    darkOutline: '1.5px solid #ECEAE3',
  },

  // Secondary/Purple
  secondary: {
    main: '#8B5CF6',
  },

  // Accent/Pink
  accent: {
    main: '#EC4899',
  },

  // Success/Green
  success: {
    background: '#F1F8F3',
    border: '#D5EBDB',
    text: '#065F46',
    main: '#10B981',
  },

  // Success/Green (Dark Mode)
  darkSuccess: {
    background: '#0a2f1f',
    border: '#1a4d32',
    text: '#4ade80',
    main: '#10B981',
  },

  // Error/Red
  error: {
    background: '#FBF3F2',
    border: '#F6DDDA',
    text: '#991B1B',
    main: '#DC2626',
    light: '#EF4444',
  },

  // Error/Red (Dark Mode)
  darkError: {
    background: '#2d1515',
    border: '#4d2020',
    text: '#f87171',
    main: '#DC2626',
    light: '#EF4444',
  },

  // Gradients
  gradients: {
    avatar: 'linear-gradient(180deg, #E7E5E4 0%, #ffffff 100%)',
    progressCircle: 'linear-gradient(to right, #3B82F6 0%, #8B5CF6 50%, #EC4899 100%)',
  },

  // Dark Gradients
  darkGradients: {
    avatar: 'linear-gradient(180deg, #3d4451 0%, #292e36 100%)',
    progressCircle: 'linear-gradient(to right, #3B82F6 0%, #8B5CF6 50%, #EC4899 100%)',
  },
} as const;

// Function to get semantic colors based on mode
export const getSemanticColors = (mode: 'light' | 'dark' = 'light') => {
  if (mode === 'dark') {
    return {
      // Text colors (dark mode)
      textPrimary: colors.darkGray[700],
      textSecondary: colors.darkGray[500],
      textTertiary: colors.darkGray[400],
      textDisabled: colors.darkGray[400],
      textBreadcrumb: colors.darkGray[450],
      textBreadcrumbActive: colors.darkGray[650],

      // Icon colors (dark mode)
      iconPrimary: colors.darkGray[550],
      iconSecondary: colors.darkGray[500],

      // Border colors (dark mode)
      borderPrimary: colors.darkGray[200],
      borderSecondary: colors.darkGray[300],
      borderFocus: colors.primary.main,
      borderError: colors.darkError.light,

      // Background colors (dark mode)
      bgPrimary: colors.darkBackground.main,
      bgSecondary: colors.darkGray[50],
      bgTertiary: colors.darkBackground.secondary,
      bgDisabled: colors.darkGray[100],
      bgHover: colors.darkGray[100],
      bgSidebar: colors.darkBackground.sidebar,
      bgInput: colors.darkBackground.sidebar,
      bgSelection: colors.selection.dark,
      bgGround: colors.darkBackground.main,
      bgGroundImage: colors.darkBackground.grid,
      bgGroundSize: colors.darkBackground.gridSize,
      outlineSelected: colors.ink.darkOutline,
      ink: colors.ink.darkMain,

      // Error colors (dark mode)
      errorBackground: colors.darkError.background,
      errorBackgroundHover: colors.darkError.border,
      errorText: colors.darkError.text,
      errorMain: colors.darkError.main,

      // Success colors (dark mode)
      successBackground: colors.darkSuccess.background,
      successText: colors.darkSuccess.text,
      successMain: colors.darkSuccess.main,
    };
  }

  // Light mode (default)
  return {
    // Text colors
    textPrimary: colors.gray[700],
    textSecondary: colors.gray[500],
    textTertiary: colors.gray[400],
    textDisabled: colors.gray[400],
    textBreadcrumb: colors.gray[450],
    textBreadcrumbActive: colors.gray[650],

    // Icon colors
    iconPrimary: colors.gray[550],
    iconSecondary: colors.gray[500],

    // Border colors
    borderPrimary: colors.gray[200],
    borderSecondary: colors.gray[300],
    borderFocus: colors.primary.main,
    borderError: colors.error.light,

    // Background colors
    bgPrimary: colors.background.white,
    bgSecondary: colors.gray[50],
    bgTertiary: colors.background.secondary,
    bgDisabled: colors.gray[100],
    bgHover: colors.gray[50],
    bgSidebar: colors.background.sidebar,
    bgInput: colors.background.white,
    bgSelection: colors.selection.light,
    // The page ground: warm grey with a faint dotted grid; cards sit white on it.
    bgGround: colors.background.main,
    bgGroundImage: colors.background.grid,
    bgGroundSize: colors.background.gridSize,
    // A selected card or active control: outlined in ink, not tinted blue.
    outlineSelected: colors.ink.outline,
    ink: colors.ink.main,

    // Error colors (light mode)
    errorBackground: colors.error.background,
    errorBackgroundHover: colors.error.border,
    errorText: colors.error.text,
    errorMain: colors.error.main,

    // Success colors (light mode)
    successBackground: colors.success.background,
    successText: colors.success.text,
    successMain: colors.success.main,
  };
};

// Default to light mode for backward compatibility
export const semanticColors = getSemanticColors('light');

// Border radius tokens
export const borderRadius = {
  xs: '8px',
  sm: '12px',
  md: '14px',
  lg: '16px',
  xl: '20px',
} as const;

// Z-index scale
export const zIndex = {
  overlay: 1050,
  navbar: 1100,
  searchInput: 1200,
  modal: 1300,
} as const;

// Box shadows
// Elevation is borders and tone, not shadow. What remains is barely there.
export const shadows = {
  button: 'none',
  card: '0 1px 2px rgba(0, 0, 0, 0.03)',
  modal: '0 16px 48px rgba(0, 0, 0, 0.12)',
  // The soft top light on an icon tile.
  inset: 'inset 0 1px 0 rgba(255, 255, 255, 0.6)',
} as const;

// Typography variants
export const typography = {
  // Geist, two weights. Hierarchy comes from size and grey, not from weight —
  // `semibold` and `bold` therefore resolve to medium so no call site has to
  // change to get the two-weight discipline.
  fontFamily: "'Geist Variable', 'Geist', system-ui, -apple-system, 'Segoe UI', sans-serif",
  fontSize: {
    xs: '10px',
    sm: '11px',
    md: '12px',
    base: '13px',
    lg: '14px',
    xl: '16px',
    '2xl': '18px',
    '3xl': '24px',
  },
  fontWeight: {
    regular: 400,
    medium: 500,
    semibold: 500,
    bold: 500,
  },
  letterSpacing: '-0.011em',
  /** For 18px and up. */
  letterSpacingDisplay: '-0.02em',
} as const;

// Animation transitions
export const transitions = {
  fast: 'all 0.15s cubic-bezier(0.4, 0, 0.2, 1)',
  normal: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  smooth: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  slow: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
} as const;

// Keyframe animations
export const animations = {
  fadeIn: {
    '@keyframes fadeIn': {
      from: { opacity: 0, transform: 'translateY(-8px)' },
      to: { opacity: 1, transform: 'translateY(0)' }
    },
    animation: 'fadeIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
  },
  slideIn: {
    '@keyframes slideIn': {
      from: { opacity: 0, transform: 'translateX(-16px)' },
      to: { opacity: 1, transform: 'translateX(0)' }
    },
    animation: 'slideIn 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
  },
  scaleIn: {
    '@keyframes scaleIn': {
      from: { opacity: 0, transform: 'scale(0.95)' },
      to: { opacity: 1, transform: 'scale(1)' }
    },
    animation: 'scaleIn 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
  },
  pulse: {
    '@keyframes pulse': {
      '0%, 100%': { opacity: 1 },
      '50%': { opacity: 0.8 }
    },
    animation: 'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite'
  }
} as const;
